(function() {
  /** @type {WeakMap<HTMLElement, {embla: any, slidesToScroll: number, align: string}>} */
  const initializedCarousels = new WeakMap();

  /**
   * Get responsive slidesToScroll based on viewport width
   * @returns {number}
   */
  function getSlidesToScroll() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      return 4; // Desktop: 4 slides visible
    } else if (window.matchMedia('(min-width: 750px)').matches) {
      return 3; // Tablet: 3 slides visible
    }
    return 1; // Mobile: 1 slide (peek effect)
  }

  /**
   * Get responsive align based on viewport width
   * @returns {string}
   */
  function getAlign() {
    return window.matchMedia('(min-width: 750px)').matches ? 'start' : 'center';
  }

  /**
   * Setup dots for carousel
   * @param {HTMLElement} carouselElement
   * @param {any} embla
   * @param {HTMLElement} dotsContainer
   */
  function setupDots(carouselElement, embla, dotsContainer) {
    if (!dotsContainer) return;

    try {
      const slides = carouselElement.querySelectorAll('.nf-product-recommendations__slide');
      
      if (!slides.length) {
        dotsContainer.innerHTML = '';
        return;
      }

      // Get the actual snap points from Embla
      let totalPages = 0;
      try {
        // Try to use scrollSnapList if available
        if (typeof embla.scrollSnapList === 'function') {
          // @ts-ignore - scrollSnapList may not be in type definitions but exists at runtime
          const snapList = embla.scrollSnapList();
          totalPages = snapList?.length || 0;
        } else {
          throw new Error('scrollSnapList not available');
        }
      } catch (e) {
        // Fallback: calculate manually if scrollSnapList isn't available
        const carouselData = initializedCarousels.get(carouselElement);
        const visibleSlidesPerPage = carouselData ? carouselData.slidesToScroll : getSlidesToScroll();
        totalPages = Math.max(1, Math.ceil(slides.length / visibleSlidesPerPage));
      }
      

      // Only show dots if there are multiple pages
      if (totalPages <= 1) {
        dotsContainer.innerHTML = '';
        return;
      }

      // Clear existing dots
      dotsContainer.innerHTML = '';

      // Create dots based on actual snap points
      // Each dot corresponds directly to a snap index (0, 1, 2, ...)
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.className = 'nf-product-recommendations__dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        
        // Each dot corresponds directly to a snap index
        const snapIndex = i;
        dot.addEventListener('click', () => {
          embla.scrollTo(snapIndex);
        });
        
        dotsContainer.appendChild(dot);
      }
    } catch (error) {
      console.warn('Product Recommendations: Error setting up dots', error);
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
      }
    }
  }

  /**
   * Update active dot state
   * @param {HTMLElement} dotsContainer
   * @param {any} embla
   */
  function updateDots(dotsContainer, embla) {
    if (!dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('.nf-product-recommendations__dot');
    if (dots.length === 0) return;

    try {
      // Get the current snap index (this is the page index when using slidesToScroll)
      const selectedSnapIndex = embla.selectedScrollSnap();
      
      // Ensure activePageIndex is within valid range
      const activePageIndex = Math.max(0, Math.min(selectedSnapIndex, dots.length - 1));
      
      dots.forEach((dot, index) => {
        if (index === activePageIndex) {
          dot.classList.add('nf-product-recommendations__dot--active');
        } else {
          dot.classList.remove('nf-product-recommendations__dot--active');
        }
      });
    } catch (error) {
      console.warn('Product Recommendations: Error updating dots', error);
    }
  }

  /**
   * Initialize carousel with Embla
   * @param {HTMLElement} carouselElement
   */
  function initCarousel(carouselElement) {
    if (!carouselElement) return;
    
    // Skip if already initialized
    if (initializedCarousels.has(carouselElement)) return;

    if (typeof EmblaCarousel === 'undefined') {
      setTimeout(() => initCarousel(carouselElement), 100);
      return;
    }

    const currentSlidesToScroll = getSlidesToScroll();
    const currentAlign = getAlign();
    
    const embla = EmblaCarousel(carouselElement, {
      loop: false,
      align: currentAlign,
      containScroll: 'trimSnaps',
      slidesToScroll: currentSlidesToScroll,
      dragFree: false
    });

    initializedCarousels.set(carouselElement, { embla, slidesToScroll: currentSlidesToScroll, align: currentAlign });

    const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
    const carouselId = carouselElement.getAttribute('data-carousel-id');
    const prevButton = carouselWrapper?.querySelector(`.nf-product-recommendations__button--prev[data-carousel-id="${carouselId}"]`);
    const nextButton = carouselWrapper?.querySelector(`.nf-product-recommendations__button--next[data-carousel-id="${carouselId}"]`);
    const dotsContainer = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`);
    

    const updateButtons = () => {
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !embla.canScrollPrev();
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !embla.canScrollNext();
      }
    };

    if (prevButton) {
      prevButton.addEventListener('click', () => embla.scrollPrev());
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => embla.scrollNext());
    }

    embla.on('select', () => {
      updateButtons();
      // Re-query dots container to ensure we have the latest reference
      const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
      const carouselId = carouselElement.getAttribute('data-carousel-id');
      const dotsContainerRefreshed = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`) || dotsContainer;
      if (dotsContainerRefreshed) {
        updateDots(dotsContainerRefreshed, embla);
      }
    });

    embla.on('init', () => {
      // Wait for Embla to fully initialize before setting up dots
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Re-query dots container in case DOM wasn't ready
          const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
          const carouselId = carouselElement.getAttribute('data-carousel-id');
          const dotsContainerRefreshed = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`) || dotsContainer;
          
          if (dotsContainerRefreshed) {
            setupDots(carouselElement, embla, dotsContainerRefreshed);
            updateButtons();
            // Ensure we're at the first position and update active dot
            embla.scrollTo(0);
            updateDots(dotsContainerRefreshed, embla);
          }
        }, 100);
      });
    });

    embla.on('reInit', () => {
      // Recalculate dots when carousel is reinitialized
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Re-query dots container in case DOM wasn't ready
          const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
          const carouselId = carouselElement.getAttribute('data-carousel-id');
          const dotsContainerRefreshed = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`) || dotsContainer;
          
          if (dotsContainerRefreshed) {
            setupDots(carouselElement, embla, dotsContainerRefreshed);
            updateButtons();
            // After dots are set up, ensure we're at the first position and update active dot
            embla.scrollTo(0);
            updateDots(dotsContainerRefreshed, embla);
          }
        }, 100);
      });
    });

    // @ts-ignore - settle event may not be in type definitions but exists at runtime
    embla.on('settle', () => {
      // Re-query dots container to ensure we have the latest reference
      const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
      const carouselId = carouselElement.getAttribute('data-carousel-id');
      const dotsContainerRefreshed = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`) || dotsContainer;
      if (dotsContainerRefreshed) {
        updateDots(dotsContainerRefreshed, embla);
      }
    });

    // Initial setup
    updateButtons();

    // Handle window resize to adjust slidesToScroll and recalculate dots
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer !== undefined) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        const carouselData = initializedCarousels.get(carouselElement);
        if (!carouselData) return;
        
        const newSlidesToScroll = getSlidesToScroll();
        const newAlign = getAlign();
        if (carouselData.slidesToScroll !== newSlidesToScroll || carouselData.align !== newAlign) {
          carouselData.embla.destroy();
          initializedCarousels.delete(carouselElement);
          initCarousel(carouselElement);
        } else {
          // If slidesToScroll didn't change, just recalculate dots in case slides changed
          const carouselWrapper = carouselElement.closest('.nf-product-recommendations-carousel-wrapper');
          const carouselId = carouselElement.getAttribute('data-carousel-id');
          const dotsContainerRefreshed = carouselWrapper?.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`);
          if (dotsContainerRefreshed) {
            setupDots(carouselElement, embla, dotsContainerRefreshed);
            updateDots(dotsContainerRefreshed, embla);
          }
        }
      }, 250);
    };
    window.addEventListener('resize', handleResize);
  }

  function initAllCarousels() {
    const carousels = document.querySelectorAll('.nf-product-recommendations-carousel:not([data-initialized])');
    carousels.forEach((carousel) => {
      if (carousel instanceof HTMLElement) {
        carousel.setAttribute('data-initialized', 'true');
        initCarousel(carousel);
      }
    });
  }

  function handleProductRecommendationsLoad() {
    // Listen for when product-recommendations custom element loads content
    const productRecommendationsElements = document.querySelectorAll('product-recommendations.product-recommendations--embla');
    
    productRecommendationsElements.forEach((element) => {
      // Check if recommendations have been loaded
      if (element.dataset.recommendationsPerformed === 'true') {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      }
    });
  }

  function handleThemeEditorUpdates() {
    // Listen for theme editor DOM updates
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
      document.addEventListener('shopify:section:reorder', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
      document.addEventListener('shopify:block:select', () => {
        setTimeout(() => {
          initAllCarousels();
        }, 100);
      });
    }
  }

  // Watch for product-recommendations element updates
  function observeProductRecommendations() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-recommendations-performed') {
          const element = mutation.target;
          if (element.dataset.recommendationsPerformed === 'true') {
            setTimeout(() => {
              initAllCarousels();
            }, 100);
          }
        }
        
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain carousel viewports
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const carousels = node.querySelectorAll?.('.nf-product-recommendations-carousel:not([data-initialized])');
              carousels?.forEach((carousel) => {
                if (carousel instanceof HTMLElement) {
                  setTimeout(() => {
                    initCarousel(carousel);
                  }, 100);
                }
              });
            }
          });
        }
      });
    });

    // Observe all product-recommendations elements
    const productRecommendationsElements = document.querySelectorAll('product-recommendations.product-recommendations--embla');
    productRecommendationsElements.forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['data-recommendations-performed'],
        childList: true,
        subtree: true
      });
    });
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllCarousels();
      handleThemeEditorUpdates();
      observeProductRecommendations();
      handleProductRecommendationsLoad();
    });
  } else {
    initAllCarousels();
    handleThemeEditorUpdates();
    observeProductRecommendations();
    handleProductRecommendationsLoad();
  }

  // Use MutationObserver for theme editor updates
  if (window.Shopify?.designMode) {
    const observer = new MutationObserver(() => {
      initAllCarousels();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
